/**
 * Achievement Types
 * TypeScript types and enums for the achievements system
 */

import { AchievementType as BEAchievementType } from 'pure-js/types/Achievement'

/**
 * Achievement level enum for tiered achievements
 */
export enum AchievementLevel {
  BRONZE = 'Bronze',
  SILVER = 'Silver',
  GOLD = 'Gold',
  PLATINUM = 'Platinum'
}

/**
 * Achievement type enum
 */
export enum AchievementType {
  SIMPLE = 'simple',
  TIERED = 'tiered'
}

/**
 * Achievement ID enum - all available achievement identifiers
 */
export enum AchievementId {
  COMPLETED_FIRST_GAME = 'completed_first_game',
  URANIUM_COLLECTED = 'uranium_collected',
  INVITATION_SEND = 'invitation_send',
  TESTER = 'tester',
  UI_MASTER = 'ui_master',
  NO_SLEEP = 'no_sleep',
  LEADER = 'leader',
  TROUBLE_MAKER = 'trouble_maker',
  DIE_MANY_TIMES = 'die_many_times',
  NO_WORK_DONE = 'no_work_done'
}

/**
 * Achievement category enum (optional, for future grouping)
 */
export enum AchievementCategory {
  GAMEPLAY = 'gameplay',
  SOCIAL = 'social',
  PROGRESS = 'progress',
  MILESTONE = 'milestone'
}

/**
 * Threshold values for tiered achievements
 */
export interface AchievementThresholds {
  Bronze: number
  Silver: number
  Gold: number
  Platinum: number
}

/**
 * Base achievement interface for simple (boolean) achievements
 */
export interface BaseAchievement {
  id: AchievementId
  name: string
  description: string
  isCompleted: boolean
  type: AchievementType.SIMPLE
  category?: AchievementCategory
  tooltip?: string // Tooltip text for hover display
}

/**
 * Tiered achievement interface for achievements with levels (0-100)
 */
export interface TieredAchievement {
  id: AchievementId
  name: string
  description: string
  type: AchievementType.TIERED
  currentValue: number
  thresholds: AchievementThresholds
  /** The level reached; none before the first one. */
  level?: AchievementLevel
  /** The level to reach next; none once the last one is reached. */
  nextLevel?: AchievementLevel
  /** 0 to 100 towards the next level. */
  progressPercent: number
  extraValue?: number
  category?: AchievementCategory
  tooltip?: string // Tooltip text for hover display
}

/**
 * Union type for all achievement types
 */
export type Achievement = BaseAchievement | TieredAchievement

/**
 * Backend response data structure for a single achievement
 */
export interface AchievementDataItem {
  value: number | boolean
  extraValue?: number
}

/**
 * Backend response format - Record of achievement ID to data
 */
export type AchievementData = Record<AchievementId, AchievementDataItem>

/**
 * Type guard to check if achievement is tiered
 */
export function isTieredAchievement(achievement: Achievement): achievement is TieredAchievement {
  return achievement.type === AchievementType.TIERED
}

/**
 * Type guard to check if achievement is simple
 */
export function isSimpleAchievement(achievement: Achievement): achievement is BaseAchievement {
  return achievement.type === AchievementType.SIMPLE
}

/**
 * Session Achievement Display Type
 * Used for displaying session achievements from BE (uses BE AchievementType directly)
 */
export interface SessionAchievementDisplay {
  id: BEAchievementType // From BE AchievementType enum
  tier: AchievementLevel // Mapped from BE AchievementTier
  title: string // From text mapping
  tooltip?: string // From text mapping
  description: string // From text mapping
}
